import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { CameraCapture, ImageCropper } from './Customers/CustomerAvatar';
import './Customers/styles/CustomerAvatar.css';

const UserAvatar = ({ user, onUserUpdate }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const photoUrl = user?.UserPhoto;
  const initials = (user?.UserFirstName?.charAt(0) || '') + (user?.UserLastName?.charAt(0) || '');

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showDropdown]);

  const handleAvatarClick = (e) => {
    e.stopPropagation();
    if (!isUploading) setShowDropdown(prev => !prev);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setShowCropper(true);
    }
    setShowDropdown(false);
    e.target.value = '';
  };

  const handleCameraCapture = (blob) => {
    const file = new File([blob], 'camera_photo.jpg', { type: 'image/jpeg' });
    setSelectedFile(file);
    setShowCamera(false);
    setShowCropper(true);
  };

  const handleCropComplete = (croppedBlob) => {
    const file = new File([croppedBlob], selectedFile?.name || 'photo.jpg', { type: 'image/jpeg' });
    uploadAvatar(file);
    setShowCropper(false);
    setSelectedFile(null);
  };

  const handleEditPhoto = () => {
    setSelectedFile(null);
    setShowCropper(true);
    setShowDropdown(false);
  };

  const uploadAvatar = async (file) => {
    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await axios.post(
        `${API_URL}/api/users/${user.UserName}/avatar`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.success && onUserUpdate) {
        onUserUpdate({ ...user, UserPhoto: res.data.photoUrl });
      }
    } catch (err) {
      console.error('User avatar upload failed:', err);
      alert(err.response?.data?.error || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const deleteAvatar = async () => {
    try {
      setIsUploading(true);
      setShowDropdown(false);
      await axios.delete(`${API_URL}/api/users/${user.UserName}/avatar`);
      if (onUserUpdate) onUserUpdate({ ...user, UserPhoto: null });
    } catch (err) {
      console.error('User avatar delete failed:', err);
      alert(err.response?.data?.error || 'Delete failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleMenuOption = (action, e) => {
    e.stopPropagation();
    switch (action) {
      case 'upload':
        fileInputRef.current?.click();
        break;
      case 'camera': {
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
          cameraInputRef.current?.click();
        } else {
          setShowCamera(true);
          setShowDropdown(false);
        }
        break;
      }
      case 'edit':
        handleEditPhoto();
        break;
      case 'delete':
        deleteAvatar();
        break;
      default:
        setShowDropdown(false);
    }
  };

  return (
    <>
      <div className="customer-avatar-container" ref={dropdownRef}>
        <div
          className={`customer-avatar ${isUploading ? 'uploading' : ''} ${showDropdown ? 'active' : ''} clickable`}
          onClick={handleAvatarClick}
          title="Click to change profile photo"
          style={{ width: '50px', height: '50px', marginRight: '15px', marginTop: '25px', background: 'linear-gradient(135deg, var(--mfg-dini-pink-dark), var(--mfg-dini-gold))', border: '2px solid var(--mfg-dini-pink-dark)' }}
        >
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={user?.UserFullName || initials}
              className="avatar-image"
              loading="lazy"
              crossOrigin="anonymous"
              referrerPolicy="no-referrer"
              onError={(e) => {
                if (e.target.src.includes('/uc?id=')) {
                  const fileId = photoUrl.match(/id=([^&]+)/)?.[1];
                  if (fileId) {
                    e.target.src = `https://lh3.googleusercontent.com/d/${fileId}`;
                    return;
                  }
                }
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}

          <div
            className="avatar-default-icon"
            style={{ display: photoUrl ? 'none' : 'flex', fontWeight: 'bold', fontSize: '14px' }}
          >
            {initials || <i className="fas fa-user" />}
          </div>

          {isUploading && (
            <div className="avatar-upload-overlay">
              <div className="upload-spinner">
                <i className="fas fa-spinner fa-spin" />
              </div>
            </div>
          )}
        </div>

        {showDropdown && (
          <div className="avatar-dropdown">
            <div className="dropdown-arrow" />
            <div className="dropdown-content">
              <button className="dropdown-option primary" onClick={(e) => handleMenuOption('camera', e)}>
                <i className="fas fa-camera" /><span>Take Photo</span>
              </button>
              <button className="dropdown-option" onClick={(e) => handleMenuOption('upload', e)}>
                <i className="fas fa-upload" /><span>Choose Photo</span>
              </button>
              {photoUrl && (
                <>
                  <button className="dropdown-option" onClick={(e) => handleMenuOption('edit', e)}>
                    <i className="fas fa-crop" /><span>Edit Photo</span>
                  </button>
                  <button className="dropdown-option delete" onClick={(e) => handleMenuOption('delete', e)}>
                    <i className="fas fa-trash" /><span>Remove Photo</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} style={{ display: 'none' }} />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} style={{ display: 'none' }} />
      </div>

      {showCamera && (
        <CameraCapture onCapture={handleCameraCapture} onCancel={() => { setShowCamera(false); setShowDropdown(false); }} />
      )}

      {showCropper && (
        <ImageCropper
          imageFile={selectedFile}
          existingImageUrl={!selectedFile ? photoUrl : null}
          onCrop={handleCropComplete}
          onCancel={() => { setShowCropper(false); setSelectedFile(null); }}
        />
      )}
    </>
  );
};

export default UserAvatar;
